import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  QAccountAccessSection,
  QBadge,
  QBox,
  QButtonGroup,
  QDivider,
  QHeading,
  QInheritedFromParentIcon,
  QLink,
  QPropertiesPanel,
  QStack,
  QText,
  QTogglePropertiesPanelButton,
  useCurrentUser,
  useFeatureFlag,
} from '@qualio/ui-components';

import {
  DocumentApprovalType,
  DocumentFormat,
  PageOrientation,
  QualioDocument,
} from '../../../../api/model/document';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { PeriodicReview as PeriodicReviewType } from '../../../../api/model/periodicReview';
import { useCompany } from '../../../../hooks/useCompany';
import { useDocumentTemplate } from '../../../../hooks/useDocumentTemplate';
import { useFormatDate } from '../../../../hooks/useFormatDate';
import { ChangeControl } from '../../../../types/DocumentCreate';
import { convertEpochToDate } from '../../../../util/DateUtils';
import { toAbsoluteUrl } from '../../../../util/UrlUtils';
import { useTrainingAssessment } from '../../TrainingAssessment/hooks/useTrainingAssessment';
import { DocumentOverviewContext } from '../Context';
import { useZoomConfig } from '../hooks/useZoomConfig';
import {
  canManageAccountAccess,
  isIssueRelated,
  shouldShowAccountAccess,
} from '../RenderLogic';
import { CCTemplateUpgradeButton } from './CCTemplateUpgradeButton';
import { ChangeRequest } from './ChangeRequest';
import styles from './DocumentProperties.module.css';
import { EditPropertiesButton } from './EditPropertiesButton';
import {
  ManageApproversProps,
  ManageMergedApprovers,
  ManageOtherApprovers,
  ManageQualityApprovers,
} from './ManageApprovers';
import { ManageEditors } from './ManageEditors';
import { ManageReviewers } from './ManageReviewers';
import { ManageTags, ManageTagsProps } from './ManageTags';
import { ManageTrainees, ManageTraineesProps } from './ManageTrainees';
import { NewTemplateVersionAvailableButton } from './NewTemplateVersionAvailableButton';
import { PeriodicReview } from './PeriodicReview';
import { SequentialApprovers } from './SequentialApprovers';
import {
  determineFileDownloadUsergroupPermissionsFromModel,
  getFileDownloadPermissionOptions,
  isMajorVersion,
} from './utils';
import { ViewIssue } from './ViewIssue';

type ChangeControlSubType = {
  changeId?: ChangeControl['id'];
  changeControlReference?: ChangeControl['change_control_reference'];
  templateType?: string;
  templateVersion?: string;
};

type DocumentPropertiesProps = {
  changeControl: ChangeControlSubType;
  periodicReview?: PeriodicReviewType;
} & ManageTraineesProps &
  ManageTagsProps &
  Pick<ManageApproversProps, 'tags' | 'groups' | 'users'>;

export const DocumentProperties = ({
  tags,
  versions,
  groups,
  users,
  changeControl,
  periodicReview,
}: DocumentPropertiesProps) => {
  const { t } = useTranslation();
  const { trainingPercentageComplete } = useFlags();
  const { qualioDocument, refetchDocumentOverviewData } = useContext(
    DocumentOverviewContext,
  );
  const currentUser = useCurrentUser();
  const { data: company } = useCompany(true);
  const qualityApprovalRequired =
    qualioDocument?.issue?.issue_step_quality_approval_required;
  const showMergedApprovers = qualityApprovalRequired === false;
  const isSequentialApproval =
    qualioDocument.approval_type === DocumentApprovalType.Sequential;
  const { shouldOpenDocPropertiesPanel } = useZoomConfig(
    qualioDocument?.orientation || PageOrientation.Portrait,
  );
  const { data: trainingAssessment } = useTrainingAssessment(
    qualioDocument?.id,
    {
      enabled: Boolean(trainingPercentageComplete),
      showErrorToast: false,
    },
  );
  const trainingPassPercentage =
    trainingAssessment?.training_assessment_completion_percent;
  const formatDate = useFormatDate();
  const featureFlags = useFeatureFlag();
  const enterpriseAccounts = Boolean(featureFlags['enterprise-accounts']);
  const { data: documentTemplate } = useDocumentTemplate(
    qualioDocument.template_id,
    enterpriseAccounts,
  );
  const canManageDocumentAccountAccess = canManageAccountAccess(
    qualioDocument,
    versions ?? [],
    tags,
  );
  const formatToText = {
    [DocumentFormat.QualioNativeDocument]: t('documentFormats.qualioDocument'),
    [DocumentFormat.DocumentUpload]: t('documentFormats.uploadedDocument'),
  };

  const sanitiseProperty = (property: any) => {
    if (!property) {
      return '-';
    }

    return property;
  };

  const formTypeFromDocumentTemplateOrType = (
    qualioDocument: QualioDocument,
  ) => {
    if (qualioDocument.document_template) {
      return `${qualioDocument.document_template.type} (v${qualioDocument.document_template.version})`;
    }
    return qualioDocument.type;
  };

  const format = (date: number | null) => {
    if (date === null) {
      return null;
    }
    return formatDate(convertEpochToDate(date));
  };

  const reviewPeriod = qualioDocument.review_period
    ? `${qualioDocument.review_period} ${
        qualioDocument.review_period > 1
          ? t('labels.months')
          : t('labels.month')
      }`
    : '-';
  const effectiveOnApproval = qualioDocument.effective_on_approval
    ? t('labels.yes')
    : t('labels.no');
  const trainingAvailable = qualioDocument.training_available
    ? t('labels.yes')
    : t('labels.no');
  const trainingRequired = qualioDocument.training_required
    ? t('labels.yes')
    : t('labels.noPriorVersionTrainingAccepted');

  return (
    <QPropertiesPanel openOnMount={shouldOpenDocPropertiesPanel}>
      <QBox
        className={styles['document-properties-variable-width']}
        fontSize="sm"
      >
        <QStack
          isInline
          justifyContent="space-between"
          spacing="2"
          alignItems="center"
          pb={2}
        >
          <QHeading fontSize="md" pt={2} pb={2}>
            {t('labels.properties')}
          </QHeading>
          <QButtonGroup>
            <EditPropertiesButton />
            <QTogglePropertiesPanelButton />
          </QButtonGroup>
        </QStack>
        <QStack spacing="4">
          <QStack spacing="1">
            <QText fontWeight="semibold">{t('labels.author')}</QText>
            <QText data-cy={'author-name'}>
              {sanitiseProperty(qualioDocument?.author?.full_name)}
            </QText>
          </QStack>
          <QStack spacing="1">
            <QText fontWeight="semibold">{t('labels.type')}</QText>
            <QStack direction="row" spacing="2" alignItems="center">
              <QText data-cy={'document-type'}>
                {sanitiseProperty(
                  formTypeFromDocumentTemplateOrType(qualioDocument),
                )}
              </QText>
              {enterpriseAccounts && documentTemplate?.parent && (
                <QInheritedFromParentIcon
                  parentName={documentTemplate.parent.name}
                  data-cy="inherited-template-icon"
                />
              )}
            </QStack>
            <NewTemplateVersionAvailableButton />
          </QStack>
          <QStack spacing="1">
            <QText fontWeight="semibold">{t('labels.format')}</QText>
            <QText data-cy={'document-format'}>
              {sanitiseProperty(formatToText[qualioDocument?.document_format])}
            </QText>
          </QStack>
          {!isIssueRelated(qualioDocument) && (
            <QStack spacing="2">
              <QText fontWeight="semibold">{t('labels.changeId')}</QText>
              {changeControl.changeId ? (
                <QBadge
                  data-cy={'change-id'}
                  copyValue={toAbsoluteUrl(
                    `/change-management/change-controls/${changeControl.changeId}`,
                  )}
                >
                  <QLink
                    isCrossMFE
                    href={`/change-management/change-controls/${changeControl.changeId}`}
                  >
                    {changeControl.changeControlReference}
                  </QLink>
                </QBadge>
              ) : (
                <QText>-</QText>
              )}
            </QStack>
          )}
          {!isIssueRelated(qualioDocument) &&
            changeControl.changeId &&
            changeControl.templateType && (
              <QStack spacing="1">
                <QText fontWeight="semibold">
                  {t('labels.changeControlTemplate')}
                </QText>
                <QText data-cy={'cc-template-type'}>
                  {sanitiseProperty(
                    `${changeControl.templateType} (v${changeControl.templateVersion})`,
                  )}
                </QText>
                <CCTemplateUpgradeButton />
              </QStack>
            )}
          <QStack spacing="1">
            <QText fontWeight="semibold">{t('labels.approvalDate')}</QText>
            <QText data-cy={'approval-date'}>
              {sanitiseProperty(format(qualioDocument.approval_date))}
            </QText>
          </QStack>
          <QStack spacing="1">
            <QText fontWeight="semibold">{t('labels.effectiveDate')}</QText>
            <QText data-cy={'effective-date'}>
              {sanitiseProperty(format(qualioDocument.effective_date))}
            </QText>
          </QStack>
          {!isIssueRelated(qualioDocument) && (
            <>
              <QStack spacing="1">
                <QText fontWeight="semibold">{t('labels.nextReview')}</QText>
                <QText data-cy={'review-date'}>
                  {sanitiseProperty(format(qualioDocument.review_date))}
                </QText>
              </QStack>
              <QStack spacing="1">
                <QText fontWeight="semibold">{t('labels.reviewPeriod')}</QText>
                <QText data-cy={'review-period'}>{reviewPeriod}</QText>
              </QStack>
              <QStack spacing="1">
                <QText fontWeight="semibold">
                  {t('forms.effectiveOnApproval')}
                </QText>
                <QText data-cy={'effective-on-approval'}>
                  {effectiveOnApproval}
                </QText>
              </QStack>
            </>
          )}
          {!isIssueRelated(qualioDocument) && (
            <>
              <QStack spacing="1">
                <QText fontWeight="semibold">
                  {t('forms.trainingAvailable')}
                </QText>
                <QText data-cy={'training-available'}>
                  {trainingAvailable}
                </QText>
              </QStack>
              {qualioDocument.training_available && (
                <QStack spacing="1">
                  <QText fontWeight="semibold">
                    {isMajorVersion(qualioDocument)
                      ? t('forms.trainingRequiredOnThisVersion')
                      : t('forms.trainingRequiredOnNextMajorVersion')}
                  </QText>
                  <QText data-cy={'training-required'}>
                    {trainingRequired}
                  </QText>
                </QStack>
              )}
              {trainingPercentageComplete &&
                trainingPassPercentage !== undefined && (
                  <QStack spacing="1">
                    <QText fontWeight="semibold">
                      {t('forms.trainingPassPercentage')}
                    </QText>
                    <QText data-cy={'training-pass-percentage'}>
                      {`${trainingPassPercentage}%`}
                    </QText>
                  </QStack>
                )}
            </>
          )}
          {qualioDocument.document_format === DocumentFormat.DocumentUpload && (
            <QStack data-cy="document-download-permissions" spacing="1">
              <QText fontWeight="semibold">
                {t('labels.downloadPermissions')}
              </QText>
              <QText data-cy="document-download-permissions-label">
                {
                  getFileDownloadPermissionOptions(t)[
                    determineFileDownloadUsergroupPermissionsFromModel(
                      qualioDocument,
                    )
                  ]
                }
              </QText>
            </QStack>
          )}
        </QStack>
        <QDivider mt="6" mb="6" />
        <ViewIssue />
        <ChangeRequest />
        <PeriodicReview periodicReview={periodicReview} />
        <ManageEditors
          editors={qualioDocument.audit_groups?.editor_group}
          groups={groups}
          users={users}
          tags={tags}
        />
        <QDivider mt="6" mb="6" />
        {isSequentialApproval && <SequentialApprovers />}
        {!isSequentialApproval &&
          (showMergedApprovers ? (
            <ManageMergedApprovers
              approvers={qualioDocument.audit_groups?.approver_group}
              tags={tags}
              groups={groups}
              users={users}
              documentCategory={qualioDocument.category}
            />
          ) : (
            <>
              <ManageQualityApprovers
                approvers={qualioDocument.audit_groups?.approver_group}
                tags={tags}
                groups={groups}
                users={users}
                documentCategory={qualioDocument.category}
              />
              <QDivider mt="6" mb="6" />
              <ManageOtherApprovers
                approvers={qualioDocument.audit_groups?.approver_group}
                tags={tags}
                groups={groups}
                users={users}
                documentCategory={qualioDocument.category}
              />
            </>
          ))}
        <QDivider mt="6" mb="6" />
        <ManageReviewers
          reviewers={qualioDocument.audit_groups?.review_group}
          tags={tags}
          groups={groups}
          users={users}
        />
        <QDivider mt="6" mb="6" />
        <ManageTags tags={tags} groups={groups} />
        {enterpriseAccounts &&
          shouldShowAccountAccess(qualioDocument, company, currentUser) && (
            <>
              <QDivider mt="6" mb="6" />
              <QAccountAccessSection
                entityType="document"
                entityMatrixId={qualioDocument.document_matrix_id}
                sharedCompanyIds={qualioDocument.shared_company_ids ?? []}
                canManage={canManageDocumentAccountAccess}
                onShared={refetchDocumentOverviewData}
              />
            </>
          )}
        {!isIssueRelated(qualioDocument) &&
          qualioDocument.training_available && (
            <>
              <QDivider mt="6" mb="6" />
              <ManageTrainees
                versions={versions}
                groups={groups}
                tags={tags}
                users={users}
              />
            </>
          )}
      </QBox>
    </QPropertiesPanel>
  );
};
